/**
 * FraudReportsMostFraudulentCampaignsPublishersPage — Trackier-style top-fraud
 * report. Topbar (title + date range), 4 KPI tiles, 2 ranked tables (worst
 * campaigns + worst publishers). Green accent (green-600/700/50). Red-600
 * confined to fraud percentages / alerts inline.
 *
 * Data source: GET /api/v1/fraud-reports-most-fraudulent-campaigns-publishers/ranked
 * called twice — once dim=campaign, once dim=publisher — with matching
 * since/until unix seconds derived from the date-range picker.
 */
import { useMemo, useState } from "react";
import { useFraudReportsMostFraudulentCampaignsPublishersQuery } from "./hooks";
import type { RankedRow } from "./api";

// ---------------------------------------------------------------------------
// Shared styles (Trackier-style: white cards, zinc-200 hairlines, 8px rounded)
// ---------------------------------------------------------------------------
const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TH =
	"px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-600";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-green-600";
const INPUT =
	"rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function isoDay(offsetDays: number): string {
	const d = new Date();
	d.setUTCDate(d.getUTCDate() - offsetDays);
	return d.toISOString().slice(0, 10);
}

function isoDayToUnix(iso: string, end = false): number | null {
	// Expect YYYY-MM-DD. Treat as UTC midnight (or 23:59:59 when end=true).
	if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
	const suffix = end ? "T23:59:59Z" : "T00:00:00Z";
	const t = Date.parse(iso + suffix);
	if (!Number.isFinite(t)) return null;
	return Math.floor(t / 1000);
}

function fmtInt(n: number): string {
	return Number.isFinite(n) ? Math.round(n).toLocaleString() : "0";
}

function fmtMoney(cents: number): string {
	if (!Number.isFinite(cents)) return "$0.00";
	return `$${(cents / 100).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}

function fraudPct(row: RankedRow): number {
	const total = row.click_count || 0;
	if (total <= 0) return 0;
	return (row.fraud_count / total) * 100;
}

function fmtPct(n: number): string {
	if (!Number.isFinite(n)) return "0%";
	return `${n.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Inline SVG icons (no external deps)
// ---------------------------------------------------------------------------
function ShieldIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M12 3 4 6v6c0 4.5 3.2 8.5 8 9 4.8-.5 8-4.5 8-9V6l-8-3Z" />
			<path d="m9 12 2 2 4-4" />
		</svg>
	);
}

function AlertIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
			<line x1="12" y1="9" x2="12" y2="13" />
			<line x1="12" y1="17" x2="12.01" y2="17" />
		</svg>
	);
}

function TrendIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<polyline points="3 17 9 11 13 15 21 7" />
			<polyline points="14 7 21 7 21 14" />
		</svg>
	);
}

function UsersIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
			<path d="M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function FraudReportsMostFraudulentCampaignsPublishersPage() {
	// Default date range: last 30 days through today (UTC).
	const [from, setFrom] = useState<string>(() => isoDay(30));
	const [to, setTo] = useState<string>(() => isoDay(0));

	const sinceUnix = useMemo(() => isoDayToUnix(from, false), [from]);
	const untilUnix = useMemo(() => isoDayToUnix(to, true), [to]);

	const campaignsQuery =
		useFraudReportsMostFraudulentCampaignsPublishersQuery({
			dim: "campaign",
			limit: 10,
			since: sinceUnix,
			until: untilUnix,
		});
	const publishersQuery =
		useFraudReportsMostFraudulentCampaignsPublishersQuery({
			dim: "publisher",
			limit: 10,
			since: sinceUnix,
			until: untilUnix,
		});

	const campaigns = campaignsQuery.data?.items ?? [];
	const publishers = publishersQuery.data?.items ?? [];

	// ------- KPI tile totals (range-scoped across both dims via campaigns) ----
	// The BE returns the same underlying events grouped differently; summing
	// fraud_count across all campaign groups reflects total flagged events.
	const totalFlaggedEvents = useMemo(
		() => campaigns.reduce((acc, r) => acc + (r.fraud_count || 0), 0),
		[campaigns],
	);
	const totalFlaggedClicks = useMemo(
		() => campaigns.reduce((acc, r) => acc + (r.click_count || 0), 0),
		[campaigns],
	);
	const totalFlaggedConv = useMemo(
		() => campaigns.reduce((acc, r) => acc + (r.conv_count || 0), 0),
		[campaigns],
	);
	const topBadCampaign: RankedRow | null = campaigns[0] ?? null;
	const topBadPublisher: RankedRow | null = publishers[0] ?? null;

	const anyLoading = campaignsQuery.isLoading || publishersQuery.isLoading;
	const anyError = campaignsQuery.error || publishersQuery.error || null;

	function applyPreset(days: number) {
		setFrom(isoDay(days));
		setTo(isoDay(0));
	}

	function onRefresh() {
		campaignsQuery.refetch();
		publishersQuery.refetch();
	}

	return (
		<main
			data-testid="fraud-reports-most-fraudulent-campaigns-publishers-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8 font-[Inter,ui-sans-serif,system-ui,sans-serif] text-zinc-900"
		>
			{/* ------------------------------------------------------------------
			    Topbar: title + date range
			    ------------------------------------------------------------------ */}
			<header className="flex flex-col gap-3 border-b border-zinc-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex items-start gap-3">
					<span className="mt-1 inline-flex h-9 w-9 flex-none items-center justify-center rounded-[8px] bg-green-50 text-green-700">
						<ShieldIcon className="h-5 w-5" />
					</span>
					<div className="flex flex-col gap-1">
						<p className="text-[11px] font-semibold uppercase tracking-wider text-green-700">
							Fraud Report
						</p>
						<h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
							Top Fraud: Campaigns & Publishers
						</h1>
						<p className="text-sm text-zinc-500">
							Ranked by flagged events within the selected date range.
						</p>
					</div>
				</div>

				<div
					className="flex flex-wrap items-end gap-2"
					data-testid="fraud-reports-most-fraudulent-campaigns-publishers-toolbar"
				>
					<label className="flex flex-col gap-1 text-[11px] font-medium text-zinc-500">
						From
						<input
							type="date"
							value={from}
							onChange={(e) => setFrom(e.target.value)}
							className={INPUT}
							aria-label="from date"
							data-testid="fraud-reports-most-fraudulent-campaigns-publishers-from"
						/>
					</label>
					<label className="flex flex-col gap-1 text-[11px] font-medium text-zinc-500">
						To
						<input
							type="date"
							value={to}
							onChange={(e) => setTo(e.target.value)}
							className={INPUT}
							aria-label="to date"
							data-testid="fraud-reports-most-fraudulent-campaigns-publishers-to"
						/>
					</label>
					<div className="flex items-end gap-1">
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => applyPreset(7)}
						>
							7d
						</button>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => applyPreset(30)}
						>
							30d
						</button>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => applyPreset(90)}
						>
							90d
						</button>
						<button
							type="button"
							className={BTN_PRIMARY}
							onClick={onRefresh}
							data-testid="fraud-reports-most-fraudulent-campaigns-publishers-refresh"
						>
							Refresh
						</button>
					</div>
				</div>
			</header>

			{/* ------------------------------------------------------------------
			    Error banner (red-600 allowed — inline alert)
			    ------------------------------------------------------------------ */}
			{anyError ? (
				<div
					role="alert"
					className="flex items-start gap-2 rounded-[8px] border border-zinc-200 bg-white p-3 text-sm text-red-600"
					data-testid="fraud-reports-most-fraudulent-campaigns-publishers-error"
				>
					<AlertIcon className="mt-0.5 h-4 w-4" />
					<span>
						Unable to load fraud report: {anyError.message}
					</span>
				</div>
			) : null}

			{/* ------------------------------------------------------------------
			    KPI tiles
			    ------------------------------------------------------------------ */}
			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
				aria-label="Fraud KPIs"
				data-testid="fraud-reports-most-fraudulent-campaigns-publishers-tiles"
			>
				<KpiTile
					label="Flagged clicks"
					value={anyLoading ? "…" : fmtInt(totalFlaggedClicks)}
					subtext={`${fmtInt(totalFlaggedEvents)} events`}
					icon={<ShieldIcon className="h-4 w-4" />}
					testid="tile-flagged-clicks"
				/>
				<KpiTile
					label="Flagged conversions"
					value={anyLoading ? "…" : fmtInt(totalFlaggedConv)}
					subtext={
						totalFlaggedClicks > 0
							? `${fmtPct((totalFlaggedConv / Math.max(totalFlaggedClicks, 1)) * 100)} of clicks`
							: "no clicks in range"
					}
					icon={<TrendIcon className="h-4 w-4" />}
					testid="tile-flagged-conv"
				/>
				<KpiTile
					label="Top bad campaign"
					value={
						anyLoading
							? "…"
							: topBadCampaign
								? topBadCampaign.key
								: "—"
					}
					subtext={
						topBadCampaign
							? `${fmtInt(topBadCampaign.fraud_count)} flagged`
							: "no data"
					}
					icon={<AlertIcon className="h-4 w-4" />}
					emphasis={topBadCampaign != null}
					testid="tile-top-bad-campaign"
				/>
				<KpiTile
					label="Top bad publisher"
					value={
						anyLoading
							? "…"
							: topBadPublisher
								? topBadPublisher.key
								: "—"
					}
					subtext={
						topBadPublisher
							? `${fmtInt(topBadPublisher.fraud_count)} flagged`
							: "no data"
					}
					icon={<UsersIcon className="h-4 w-4" />}
					emphasis={topBadPublisher != null}
					testid="tile-top-bad-publisher"
				/>
			</section>

			{/* ------------------------------------------------------------------
			    Worst campaigns + worst publishers tables
			    ------------------------------------------------------------------ */}
			<section className="grid grid-cols-1 gap-6 xl:grid-cols-2">
				<RankedTable
					title="Worst campaigns"
					keyLabel="Campaign"
					rows={campaigns}
					isLoading={campaignsQuery.isLoading}
					hasError={campaignsQuery.error != null}
					testid="fraud-reports-most-fraudulent-campaigns-publishers-campaigns-table"
					loadingTestid="fraud-reports-most-fraudulent-campaigns-publishers-campaigns-loading"
					emptyTestid="fraud-reports-most-fraudulent-campaigns-publishers-campaigns-empty"
					actionLabel="Review"
				/>
				<RankedTable
					title="Worst publishers"
					keyLabel="Publisher"
					rows={publishers}
					isLoading={publishersQuery.isLoading}
					hasError={publishersQuery.error != null}
					testid="fraud-reports-most-fraudulent-campaigns-publishers-publishers-table"
					loadingTestid="fraud-reports-most-fraudulent-campaigns-publishers-publishers-loading"
					emptyTestid="fraud-reports-most-fraudulent-campaigns-publishers-publishers-empty"
					actionLabel="Investigate"
				/>
			</section>
		</main>
	);
}

export default FraudReportsMostFraudulentCampaignsPublishersPage;

// ---------------------------------------------------------------------------
// KPI tile sub-component
// ---------------------------------------------------------------------------
interface KpiTileProps {
	label: string;
	value: string;
	subtext: string;
	icon: React.ReactNode;
	emphasis?: boolean;
	testid: string;
}

function KpiTile({ label, value, subtext, icon, emphasis, testid }: KpiTileProps) {
	return (
		<div
			className={`${CARD} flex flex-col gap-2`}
			data-testid={`fraud-reports-most-fraudulent-campaigns-publishers-${testid}`}
		>
			<div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
				<span
					className={`inline-flex h-6 w-6 items-center justify-center rounded-[6px] ${
						emphasis ? "bg-green-50 text-green-700" : "bg-zinc-100 text-zinc-600"
					}`}
				>
					{icon}
				</span>
				<span>{label}</span>
			</div>
			<div className="truncate text-2xl font-semibold tabular-nums text-zinc-900">
				{value}
			</div>
			<div className="truncate text-xs text-zinc-500">{subtext}</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Ranked table sub-component
// ---------------------------------------------------------------------------
interface RankedTableProps {
	title: string;
	keyLabel: string;
	rows: RankedRow[];
	isLoading: boolean;
	hasError: boolean;
	testid: string;
	loadingTestid: string;
	emptyTestid: string;
	actionLabel: string;
}

function RankedTable({
	title,
	keyLabel,
	rows,
	isLoading,
	hasError,
	testid,
	loadingTestid,
	emptyTestid,
	actionLabel,
}: RankedTableProps) {
	return (
		<div className={CARD}>
			<div className="mb-3 flex items-center justify-between border-b border-zinc-200 pb-3">
				<h2 className="text-sm font-semibold text-zinc-900">{title}</h2>
				<span className="text-[11px] font-medium uppercase tracking-wider text-green-700">
					Top {rows.length || 10}
				</span>
			</div>

			{isLoading ? (
				<p
					className="py-6 text-center text-sm text-zinc-500"
					data-testid={loadingTestid}
				>
					Loading ranked fraud data…
				</p>
			) : hasError ? null : rows.length === 0 ? (
				<p
					className="py-6 text-center text-sm text-zinc-500"
					data-testid={emptyTestid}
				>
					No flagged activity for this range.
				</p>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse"
						data-testid={testid}
					>
						<thead className="bg-zinc-50">
							<tr>
								<th className={`${TH} w-10`}>#</th>
								<th className={TH}>{keyLabel}</th>
								<th className={`${TH} text-right`}>Fraud %</th>
								<th className={`${TH} text-right`}>Flagged clicks</th>
								<th className={`${TH} text-right`}>Flagged conv.</th>
								<th className={`${TH} text-right`}>Revenue</th>
								<th className={`${TH} text-right`}>Action</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{rows.map((row, idx) => {
								const pct = fraudPct(row);
								return (
									<tr
										key={`${row.dim}-${row.key}`}
										data-testid={`${testid}-row-${row.key}`}
									>
										<td className={`${TD} text-zinc-500`}>{idx + 1}</td>
										<td className={`${TD} font-medium`}>{row.key}</td>
										<td className={`${TD} text-right font-semibold tabular-nums text-red-600`}>
											{fmtPct(pct)}
										</td>
										<td className={`${TD} text-right tabular-nums`}>
											{fmtInt(row.click_count)}
										</td>
										<td className={`${TD} text-right tabular-nums`}>
											{fmtInt(row.conv_count)}
										</td>
										<td className={`${TD} text-right tabular-nums`}>
											{fmtMoney(row.revenue_cents)}
										</td>
										<td className={`${TD} text-right`}>
											<button
												type="button"
												className="rounded-[6px] border border-green-700 bg-white px-2.5 py-1 text-[11px] font-medium text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-600"
											>
												{actionLabel}
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
